import { useStudioStore } from '../../stores/studioStore';
import { getEngine } from '../../audio/engineBridge';
import type { WarpMode } from '@melodinai/types';

const MODES: { id: WarpMode | 'preview' | 'warp'; label: string }[] = [
  { id: 'preview', label: 'Preview' },
  { id: 'warp', label: 'Warp' },
  { id: 'beats', label: 'Beats' },
  { id: 'tones', label: 'Tones' },
  { id: 'texture', label: 'Texture' },
  { id: 'repitch', label: 'Re-Pitch' },
  { id: 'complex', label: 'Complex' },
  { id: 'complexPro', label: 'Complex Pro' },
];

/**
 * React chrome for Ableton-style Clip View.
 * Waveform + warp DSP stay in the legacy engine (canvas + worker).
 */
export function AudioClipEditor() {
  const clip = useStudioStore((s) => s.selectedAudioClip);
  if (!clip) return null;

  const engine = getEngine();
  const setMode = (mode: WarpMode) => engine.updateAudioClip?.({ warpMode: mode, warpEnabled: true });

  return (
    <div className="flex min-h-[160px] flex-col border-t border-daw-border bg-daw-surface">
      <div className="flex flex-wrap gap-1 border-b border-daw-border px-2 py-1">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            className={
              m.id === clip.warpMode || (m.id === 'warp' && clip.warpEnabled)
                ? 'rounded bg-daw-accent2/20 px-2 py-0.5 text-[10px] text-daw-accent2'
                : 'rounded px-2 py-0.5 text-[10px] text-daw-muted'
            }
            onClick={() => {
              if (m.id === 'preview') engine.previewSelectedAudioClip?.();
              else if (m.id !== 'warp') setMode(m.id);
              engine.openAudioEditor?.();
            }}
          >
            {m.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 px-2 py-1 text-[10px] text-daw-muted">
        <button type="button" className="rounded border border-daw-border px-2 py-0.5" onClick={() => engine.previewSelectedAudioClip?.()}>
          Preview
        </button>
        <span>{clip.detectedBpm ? `${clip.detectedBpm} BPM` : 'BPM —'}</span>
        <span>T{clip.transpose >= 0 ? '+' : ''}{clip.transpose}</span>
        <span>{clip.pitch}¢</span>
        <span>{clip.reverse ? 'REV' : 'FWD'}</span>
        <span>{clip.markers.length} markers</span>
        <span className="ml-auto truncate text-daw-text">{clip.fileId || clip.id}</span>
      </div>
      <div id="sdproLegacyAudioHost" className="min-h-[120px] flex-1" data-host="audio-clip" />
    </div>
  );
}
